/**
 * Main App Component
 */

import { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from './components/Layout';
import { Login } from './pages/Login';
import { ToastProvider } from './components/Toast';
import { api } from './lib/api';

// Lazy load pages for better performance
const Dashboard = lazy(() => import('./pages/Dashboard'));
const AuditLogs = lazy(() => import('./pages/AuditLogs'));
const ModerationRules = lazy(() => import('./pages/ModerationRules'));
const ClientsTokens = lazy(() => import('./pages/ClientsTokens'));
const SystemSettings = lazy(() => import('./pages/SystemSettings'));
const UpstreamApis = lazy(() => import('./pages/UpstreamApis'));

// Loading fallback
function PageLoader() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="spinner" />
    </div>
  );
}

export function App() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    // Check if user has a valid authentication cookie
    const checkAuth = async () => {
      try {
        // Try to get current user info (will use cookie automatically)
        await api.getCurrentUser();
        
        // If successful, user is authenticated
        api.setAuthenticationState(true);
        setIsAuthenticated(true);
      } catch {
        // Cookie invalid, expired, or not present
        api.setAuthenticationState(false);
        setIsAuthenticated(false);
      }
    };

    checkAuth();
  }, []);

  // Show loading while checking auth
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="spinner" />
      </div>
    );
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <Login onLogin={() => setIsAuthenticated(true)} />;
  }

  const handleLogout = () => {
    api.setAuthenticationState(false);
    setIsAuthenticated(false);
  };

  return (
    <ToastProvider>
      <BrowserRouter>
        <Layout onLogout={handleLogout}>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/logs" element={<AuditLogs />} />
              <Route path="/rules" element={<ModerationRules />} />
              <Route path="/clients" element={<ClientsTokens />} />
              <Route path="/upstreams" element={<UpstreamApis />} />
              <Route path="/settings" element={<SystemSettings />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </Layout>
      </BrowserRouter>
    </ToastProvider>
  );
}

export default App;

